/* /documents — port of prototype pgDocuments (pages2.js). */
import { useState } from "react";
import { useAllEmployees, useExpiringDocuments, useToday } from "../../data/hooks";
import { useI18n } from "../../i18n";
import { useSession } from "../../app/session";
import { Button, ErrorBox, Kpi, PageHeader, SearchBox, Seg, Skeleton, useToast } from "../../ui";
import DocTable, { type DocRow } from "./components/DocTable";
import { canWriteHr, withEmployees } from "./components/shared";

type Win = "30" | "60" | "90" | "all";

export default function Documents() {
  const i = useI18n();
  const { t, L } = i;
  const { role } = useSession();
  const toast = useToast();
  const today = useToday();
  const [win, setWin] = useState<Win>("90");
  const [q, setQ] = useState("");
  const docsQ = useExpiringDocuments(100000);
  const empsQ = useAllEmployees();

  const err = docsQ.error || empsQ.error;
  if (err) return <ErrorBox error={err} onRetry={() => { docsQ.refetch(); empsQ.refetch(); }} />;
  if (!docsQ.data || !empsQ.data) return <Skeleton />;

  const all: DocRow[] = withEmployees(docsQ.data, empsQ.data).map(({ e, d }) => ({ e, kind: d.kind, number: d.number, expiry: d.expiry, daysLeft: d.daysLeft }));
  const max = win === "all" ? Infinity : Number(win);
  const needle = q.trim().toLowerCase();
  const list = all.filter((x) => x.daysLeft <= max && (!needle || [x.e.nameEn, x.e.nameAr, x.kind, t(x.kind), x.number, x.e.code].join(" ").toLowerCase().includes(needle)));
  const c = (a: number, b: number) => all.filter((x) => x.daysLeft >= a && x.daysLeft <= b).length;
  const due = c(-Infinity, 30);

  return (
    <div data-testid="documents">
      <PageHeader
        title={t("Documents & expiries")}
        sub={L("Iqamas, passports, work permits, visas and insurance · synced with Muqeem", "الإقامات والجوازات ورخص العمل والتأشيرات والتأمين · متزامنة مع مقيم")}
        actions={<>
          <Button icon="upload" onClick={() => toast(L("Open an employee's Documents tab to upload a file", "افتح تبويب الوثائق في ملف الموظف لرفع ملف"), "info")} data-testid="btn-upload">{t("Upload")}</Button>
          {canWriteHr(role) && <Button variant="pri" icon="refresh" onClick={() => toast(due ? L(due + " renewals sent to Muqeem", "تم إرسال " + due + " طلبات تجديد إلى مقيم") : L("Nothing due in the next 30 days", "لا توجد وثائق مستحقة خلال 30 يومًا"))} data-testid="btn-renew-all">{L("Renew all due", "تجديد كل المستحق")}</Button>}
        </>}
      />
      <div className="g g-4 mb" data-testid="doc-kpis">
        <Kpi testId="kpi-overdue" label={t("Overdue")} value={String(c(-Infinity, -1))} icon="alert" tone="bad" foot={L("fines may apply", "قد تُفرض غرامات")} />
        <Kpi testId="kpi-30" label={L("≤ 30 days", "≤ 30 يومًا")} value={String(c(0, 30))} icon="clock" tone="warn" />
        <Kpi testId="kpi-60" label={L("31–60 days", "31–60 يومًا")} value={String(c(31, 60))} icon="calendar" tone="info" />
        <Kpi testId="kpi-90" label={L("61–90 days", "61–90 يومًا")} value={String(c(61, 90))} icon="check" />
      </div>
      <section className="card">
        <div className="toolbar">
          <SearchBox value={q} onChange={setQ} placeholder={L("Search employee or document…", "ابحث عن موظف أو وثيقة…")} testId="doc-search" />
          <Seg<Win> label={L("Expiry window (days)", "فترة الانتهاء (بالأيام)")} value={win} onChange={setWin} items={[["30", "30"], ["60", "60"], ["90", "90"], ["all", t("All")]]} />
        </div>
        <DocTable rows={list} withEmp today={today} canRenew={canWriteHr(role)} />
      </section>
    </div>
  );
}
